import { createAsyncThunk } from '@reduxjs/toolkit';

import type { BaseAnalyticsData } from '@/pages/AppAnalytics/AppAnalytics';
import { AppAnalyticsSnowflakeService, SnowflakeQueries } from '@/pages/AppAnalytics/AppAnalyticsSnowflakeService';
import { FuelEntityType, FuelPortalService } from '@/services';
import type { Channel, Item } from '@/models';
import { dateUtils } from '@/utils/dateUtils';

import type { ExportAnalyticsJob } from './ExportAnalyticsJob';
import { removeJob, updateJob } from './ExportAnalyticsState';

const BATCH_SIZE = 100;
const DATE_FORMAT = 'MMM/dd/yyyy';

export const processExportContentAnalyticsJob = createAsyncThunk(
  'exportAnalyticsJobs/processExportContentAnalyticsJob',
  async (job: ExportAnalyticsJob, { dispatch, getState }) => {
    const service = AppAnalyticsSnowflakeService();
    const { params } = job;
    const range = {
      from: dateUtils.format(params.fromDate, params.timezone, DATE_FORMAT),
      to: dateUtils.format(params.toDate, params.timezone, DATE_FORMAT),
    };

    const updateMessage = (message: string) => {
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));
    updateMessage(`Requesting Snowflake data...`);

    const data = await service.getData(SnowflakeQueries.content, params);
    const total = data.length;
    const map: Record<string, { views: number; watchTime: number; averageWatchTime: number }> = {};
    const clipIds = [];
    for (const row of data) {
      const key = row.video;
      if (key) {
        clipIds.push(key);
        map[key] = {
          views: row.count,
          watchTime: row.duration,
          averageWatchTime: row.average,
        };
      }
    }

    job.jobStatus = 'InProgress';
    job.progress = 20;
    dispatch(updateJob(job));

    // populate all clips data
    updateMessage(`Populating clip data`);
    let csv =
      'Title, ID, Type, Series, Season, Duration (seconds), Views, Watch Time (seconds), Average Watch Time (seconds), From (UTC), To (UTC)\n';
    let batch = [];
    for await (const clipId of clipIds) {
      if (batch.length === BATCH_SIZE) {
        const update = await processBatch(batch, map, range);
        csv += update;
        batch = [];

        const progress = (BATCH_SIZE / total) * 60 + 20;
        job.progress = progress;
        dispatch(updateJob(job));
      }
      batch.push(clipId);
    }
    const update = await processBatch(batch, map, range);
    csv += update;
    job.progress = 80;
    dispatch(updateJob(job));

    // export data as csv
    updateMessage(`Exporting data as CSV`);
    job.progress = 85;
    dispatch(updateJob(job));
    downloadCSV(csv, `content-analytics-${range.from}-to-${range.to}.csv`);

    // finalize
    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    return true;
  }
);

export const processExportLiveChannelsAnalyticsJob = createAsyncThunk(
  'exportAnalyticsJobs/processExportLiveChannelsAnalyticsJob',
  async (job: ExportAnalyticsJob, { dispatch, getState }) => {
    const service = AppAnalyticsSnowflakeService();
    const { params } = job;
    const range = {
      from: dateUtils.format(params.fromDate, params.timezone, DATE_FORMAT),
      to: dateUtils.format(params.toDate, params.timezone, DATE_FORMAT),
    };

    const updateMessage = (message: string) => {
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));
    updateMessage(`Requesting Snowflake data...`);

    const data = await service.getData(SnowflakeQueries.liveChannel, params);

    job.jobStatus = 'InProgress';
    job.progress = 20;
    dispatch(updateJob(job));

    let csv = 'Channel, Views, Watch Time (seconds), Average Watch Time (seconds), From (UTC), To (UTC)\n';

    const progressRange = 60;
    for await (const row of data) {
      const channelId = row.channel;
      const channel = await FuelPortalService.getEntityById<Channel>(FuelEntityType.Channel, channelId);
      const channelTitle = channel?.name || 'Unknown Channel';

      let line = '';
      line += `${channelTitle},`;
      line += `${row.count},`;
      line += `${row.duration},`;
      line += `${row.average},`;
      line += `${range.from},${range.to}\n`;

      csv += line;

      const progress = (data.indexOf(row) / data.length) * progressRange + 20;
      job.progress = progress;
      dispatch(updateJob(job));
    }

    // export data as csv
    updateMessage(`Exporting data as CSV`);
    job.progress = 85;
    dispatch(updateJob(job));
    downloadCSV(csv, `live-channels-analytics-${range.from}-to-${range.to}.csv`);

    // finalize
    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    return true;
  }
);

export const processExportWatchAnalyticsJob = createAsyncThunk(
  'exportAnalyticsJobs/processExportWatchAnalyticsJob',
  async (job: ExportAnalyticsJob, { dispatch }) => {
    const service = AppAnalyticsSnowflakeService();
    const { params } = job;
    const range = {
      from: dateUtils.format(params.fromDate, params.timezone, DATE_FORMAT),
      to: dateUtils.format(params.toDate, params.timezone, DATE_FORMAT),
    };

    const updateMessage = (message: string) => {
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));
    updateMessage(`Requesting Snowflake data...`);

    const data = await service.getData(SnowflakeQueries.videoViews, params) as BaseAnalyticsData[];

    job.jobStatus = 'InProgress';
    job.progress = 20;
    dispatch(updateJob(job));

    // Build a lookup from group ID → display name using the resolved groupByList
    const groupLabelMap = new Map<string, string>(
      (params.groupByList || []).map((g) => [g.id, g.alias || g.name])
    );

    const groupByLabel =
      params.groupBy === 'channel' ? 'Channel'
      : params.groupBy === 'video' ? 'Video'
      : params.groupBy === 'category' ? 'Category'
      : 'Site';

    let csv = `Date,${groupByLabel},Views,Watch Time (seconds),Average Watch Time (seconds),From (UTC),To (UTC)\n`;

    const progressRange = 60;
    data.forEach((row, i) => {
      const date = (row.eventdate as string)?.substring(0, 10) ?? '';
      const groupId = String(row.site ?? '');
      const displayName = groupLabelMap.get(groupId) || groupId;

      csv += `${date},${displayName},${row.total ?? 0},${row.duration ?? 0},${row.average ?? 0},${range.from},${range.to}\n`;

      job.progress = ((i + 1) / data.length) * progressRange + 20;
      dispatch(updateJob(job));
    });

    job.progress = 80;
    dispatch(updateJob(job));

    updateMessage(`Exporting data as CSV`);
    job.progress = 85;
    dispatch(updateJob(job));
    downloadCSV(csv, `watch-analytics-${range.from}-to-${range.to}.csv`);

    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    return true;
  }
);

// helper fns
function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

async function processBatch(
  clipIds: string[],
  map: Record<string, { views: number; watchTime: number; averageWatchTime: number }>,
  range: { from: string; to: string }
) {
  const clips = await FuelPortalService.batchGet<Item[]>(FuelEntityType.Clip, clipIds);
  let update = '';

  for (const clip of clips) {
    const c = clip as any;
    const clipId = c.id;
    let row = '';
    const match = map[clipId];
    if (match) {
      const clipDuration = c.data?.technicalMetadata?.meta?.duration;
      // update csv with clip data
      row += `${c.title?.trim() ?? ''},`;
      row += `${clipId},`;
      row += `${c.type || ''},`;
      row += `${c.series || ''},`;
      row += `${c.season || ''},`;
      row += `${clipDuration},`;
      row += `${match.views},`;
      row += `${match.watchTime},`;
      row += `${match.averageWatchTime},`;
      row += `${range.from},${range.to}\n`;

      update += row;
    }
  }

  return update;
}
